import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import useAppSelector from '@hooks/useAppSelector';
import { getPhoneNumber } from '@reducers/appReducer';

export const DEFAULT_ERROR_TITLE_ID = 'common.modal.failure';
const RECAPTCHA_ERROR_TITLE_ID = 'common.recaptcha.verificationFailed.title';
const RECAPTCHA_ERROR_MESSAGE_ID = 'common.recaptcha.verificationFailed';

/**
 * Centralizes the `errorMessage`/`errorTitleId` pair used to drive `ErrorModal`
 * across the anonymous/referral flows, so the reCAPTCHA-specific title can
 * never be left stale after a subsequent, non-reCAPTCHA error is shown.
 */
export const useReCaptchaErrorModal = () => {
    const intl = useIntl();
    const phone = useAppSelector(getPhoneNumber);
    const [errorMessage, setErrorMessage] = useState('');
    const [errorTitleId, setErrorTitleId] = useState(DEFAULT_ERROR_TITLE_ID);

    const showError = useCallback((message: string) => {
        setErrorTitleId(DEFAULT_ERROR_TITLE_ID);
        setErrorMessage(message);
    }, []);

    const showRecaptchaError = useCallback(() => {
        setErrorTitleId(RECAPTCHA_ERROR_TITLE_ID);
        setErrorMessage(intl.formatMessage({ id: RECAPTCHA_ERROR_MESSAGE_ID }, { phone: phone ?? '' }));
    }, [intl, phone]);

    const resetError = useCallback(() => {
        setErrorTitleId(DEFAULT_ERROR_TITLE_ID);
        setErrorMessage('');
    }, []);

    return { errorMessage, errorTitleId, showError, showRecaptchaError, resetError };
};

export default useReCaptchaErrorModal;
